(() => {
  const STORAGE_KEY = 'gal-pack-frere-v2';

  const money = (cents) => {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.Galactica?.moneyFormat);
    }
    return `${(Number(cents) / 100).toFixed(2).replace('.', ',')} €`;
  };

  const track = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent('gal:analytics', { detail: { name, ...detail } }));
  };

  const parseNum = (v) => {
    if (v == null || v === '') return null;
    const n = Number(String(v).replace(',', '.').replace('%', '').trim());
    return Number.isFinite(n) ? n : null;
  };

  const FAMILY_META = {
    greenhouse: { label: 'GREENHOUSE', short: 'greenhouse', guide: 'Greenhouse' },
    indoor: { label: 'INDOOR', short: 'indoor', guide: 'Indoor' },
    resin: { label: 'RÉSINES', short: 'résines', guide: 'Résines' },
    rock: { label: 'ROCKS', short: 'rocks', guide: 'Rocks' },
  };

  class GalPackBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === '1') return;
      this.dataset.ready = '1';

      this.dialog = this.closest('[data-gal-pack]');
      this.config = this.readJSON('[data-gal-pack-config]') || {};
      this.catalog = this.readJSON('[data-gal-pack-catalog]') || {
        greenhouse: [],
        indoor: [],
        resin: [],
        rock: [],
      };
      this.tariffs = this.readJSON('[data-gal-pack-tariffs]') || [];

      this.totalGrams = Number(this.config.totalGrams || 100);
      this.gramSizes = (this.config.gramSizes || [2, 5, 10]).map(Number);
      this.maxPerProduct = Number(this.config.maxPerProduct || 10);
      const catMin = Number(this.config.categoryMin || 20);
      const catMax = Number(this.config.categoryMax || 30);
      this.minCat = {
        greenhouse: catMin,
        indoor: catMin,
        resin: catMin,
        rock: catMin,
      };
      this.maxCat = {
        greenhouse: catMax,
        indoor: catMax,
        resin: catMax,
        rock: catMax,
      };
      this.targetSaving = Number(this.config.targetSaving || 0.25);
      this.marginFloor = Number(this.config.marginFloor || 0.45);
      this.costs = {
        FLOWER: parseNum(this.config.costFlower) ?? 1.8,
        RESIN: parseNum(this.config.costResin) ?? 1.2,
        ROCK: parseNum(this.config.costRock) ?? 1.2,
      };
      this.meterDuration = Number(this.config.meterDuration || 1100);
      this.familyImages = this.config.familyImages || {};

      this.selections = new Map();
      this.stepIndex = 0;
      this.phase = 'loading';
      this.pricing = null;

      this.cacheEls();
      this.bind();
      this.tryResumeBanner();
    }

    readJSON(sel) {
      const el = this.querySelector(sel);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent || 'null');
      } catch {
        return null;
      }
    }

    cacheEls() {
      this.els = {
        loading: this.querySelector('[data-gal-pack-loading]'),
        intro: this.querySelector('[data-gal-pack-intro]'),
        build: this.querySelector('[data-gal-pack-build]'),
        calc: this.querySelector('[data-gal-pack-calc]'),
        result: this.querySelector('[data-gal-pack-result]'),
        tabs: [...this.querySelectorAll('[data-gal-pack-tab]')],
        grid: this.querySelector('[data-gal-pack-grid]'),
        status: this.querySelector('[data-gal-pack-status]'),
        guide: this.querySelector('[data-gal-pack-guide]'),
        stepHead: this.querySelector('[data-gal-pack-step-head]'),
        resume: this.querySelector('[data-gal-pack-resume]'),
        start: this.querySelector('[data-gal-pack-start]'),
        next: this.querySelector('[data-gal-pack-next]'),
        prev: this.querySelector('[data-gal-pack-prev]'),
        calculate: this.querySelector('[data-gal-pack-calculate]'),
        edit: this.querySelector('[data-gal-pack-edit]'),
        add: this.querySelector('[data-gal-pack-add]'),
        resultBody: this.querySelector('[data-gal-pack-result-body]'),
      };
    }

    bind() {
      this.els.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const idx = Number(tab.dataset.galPackTab);
          if (Number.isFinite(idx)) this.goStep(idx);
        });
      });
      this.els.prev?.addEventListener('click', () => {
        if (this.stepIndex === 0) {
          this.phase = 'intro';
          this.render();
          return;
        }
        this.goStep(Math.max(0, this.stepIndex - 1));
      });
      this.els.next?.addEventListener('click', () => this.goStep(Math.min(4, this.stepIndex + 1)));
      this.els.calculate?.addEventListener('click', () => this.runFinalCalculation());
      this.els.edit?.addEventListener('click', () => this.backToBuild());
      this.els.add?.addEventListener('click', () => this.addToCart());
      this.els.start?.addEventListener('click', () => this.beginBuild(0));
      this.querySelectorAll('[data-gal-pack-start-family]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const key = btn.dataset.galPackStartFamily;
          const idx = this.familyKeys().indexOf(key);
          this.beginBuild(idx >= 0 ? idx : 0);
        });
      });
      this.els.resume?.addEventListener('click', () => {
        this.loadState();
        this.phase = 'build';
        this.render();
        if (this.els.resume) this.els.resume.hidden = true;
      });
      this.querySelector('[data-gal-pack-reset]')?.addEventListener('click', () => {
        this.selections.clear();
        this.pricing = null;
        this.saveState();
        this.goStep(0);
      });
    }

    beginBuild(step = 0) {
      this.stepIndex = step;
      this.phase = 'build';
      this.render();
      track('pack_builder_start', { step });
    }

    reset() {
      this.stepIndex = 0;
      this.phase = 'loading';
      this.pricing = null;
      this.els.resume && (this.els.resume.hidden = !this.hasSavedState());
      this.render();
      this.playIntroMeter();
    }

    openWithMeter() {
      this.reset();
    }

    async playIntroMeter() {
      this.phase = 'loading';
      this.render();
      const host = this.els.loading?.querySelector('[data-gal-needle]') || this.els.loading;
      const play = window.Galactica?.playNeedleMeter;
      try {
        if (typeof play === 'function' && host) await play(host, { duration: this.meterDuration });
        else await new Promise((r) => setTimeout(r, this.meterDuration));
      } catch {
        /* continue */
      }
      if (this.els.resume) this.els.resume.hidden = !this.hasSavedState();
      this.phase = 'intro';
      this.render();
      track('pack_builder_open');
    }

    familyKeys() {
      return ['greenhouse', 'indoor', 'resin', 'rock'];
    }

    stepFamily(idx) {
      return this.familyKeys()[idx] || null;
    }

    goStep(idx) {
      this.stepIndex = Math.max(0, Math.min(4, idx));
      this.phase = 'build';
      this.render();
    }

    backToBuild() {
      this.phase = 'build';
      this.stepIndex = 4;
      this.render();
    }

    selectedList() {
      return [...this.selections.values()];
    }

    totalWeight() {
      return this.selectedList().reduce((s, x) => s + x.grams, 0);
    }

    familyWeights() {
      const w = { greenhouse: 0, indoor: 0, resin: 0, rock: 0 };
      for (const item of this.selectedList()) w[item.family] += item.grams;
      return w;
    }

    simulate(family, productId, newGrams) {
      const w = this.familyWeights();
      const cur = this.selections.get(productId)?.grams || 0;
      const prevFamily = this.selections.get(productId)?.family;
      if (prevFamily && prevFamily !== family) {
        w[prevFamily] = Math.max(0, w[prevFamily] - cur);
        w[family] += newGrams;
      } else {
        w[family] = w[family] - cur + newGrams;
      }
      return w;
    }

    isFeasible(weights) {
      const keys = this.familyKeys();
      for (const f of keys) {
        if (weights[f] < 0 || weights[f] > this.maxCat[f]) return false;
      }
      const total = keys.reduce((s, f) => s + weights[f], 0);
      if (total > this.totalGrams) return false;

      let deficit = 0;
      for (const f of keys) deficit += Math.max(0, this.minCat[f] - weights[f]);
      if (total + deficit > this.totalGrams) return false;

      const lower = keys.reduce((s, f) => s + Math.max(weights[f], this.minCat[f]), 0);
      if (lower > this.totalGrams) return false;

      let maxAdd = 0;
      for (const f of keys) maxAdd += Math.max(0, this.maxCat[f] - weights[f]);
      if (total + maxAdd < this.totalGrams) return false;

      return true;
    }

    canSetGrams(product, family, grams) {
      if (!product?.available && grams > 0) return false;
      if (grams < 0) return false;
      if (grams > 0 && !this.gramSizes.includes(grams)) return false;
      if (grams > this.maxPerProduct) return false;
      // Prevent selecting same product in another family (map key is product id)
      const existing = this.selections.get(product.id);
      if (existing && existing.family !== family && grams > 0) return false;
      return this.isFeasible(this.simulate(family, product.id, grams));
    }

    requirementsMet() {
      const w = this.familyWeights();
      if (this.totalWeight() !== this.totalGrams) return false;
      return this.familyKeys().every((f) => w[f] >= this.minCat[f] && w[f] <= this.maxCat[f]);
    }

    guidanceText() {
      const w = this.familyWeights();
      const left = Math.max(0, this.totalGrams - this.totalWeight());
      const needs = [];
      for (const f of this.familyKeys()) {
        const need = this.minCat[f] - w[f];
        if (need > 0) needs.push(`au moins ${need}G de ${FAMILY_META[f].short}`);
      }
      let msg = `Il te reste ${left}G à répartir.`;
      if (needs.length === 1) msg += ` Ajoute encore ${needs[0]}.`;
      else if (needs.length > 1) {
        const last = needs.pop();
        msg += ` Ajoute encore ${needs.join(', ')} et ${last}.`;
      } else if (left > 0) {
        msg += ' Tu peux encore affiner jusqu’aux maximums de famille.';
      } else {
        msg = 'Pack équilibré — prêt à calculer.';
      }
      return msg;
    }

    costPerGram(family) {
      if (family === 'rock') return this.costs.ROCK;
      if (family === 'resin') return this.costs.RESIN;
      return this.costs.FLOWER;
    }

    computePricing() {
      const items = this.selectedList();
      let referenceTotal = 0;
      let totalCost = 0;
      const lines = items.map((item) => {
        const p10 = item.product.p10;
        const ref = (p10 / 10) * item.grams;
        const cost = this.costPerGram(item.family) * item.grams;
        referenceTotal += ref;
        totalCost += cost;
        return {
          id: item.product.id,
          handle: item.product.handle,
          title: item.product.title,
          image: item.product.image,
          family: item.family,
          grams: item.grams,
          p10,
          reference: ref,
          cost,
        };
      });

      const target = referenceTotal * (1 - this.targetSaving);
      const marginSafe = this.marginFloor >= 1 ? totalCost : totalCost / (1 - this.marginFloor);
      let finalPrice = Math.max(target, marginSafe);
      const tariff = this.pickTariff(finalPrice);
      finalPrice = tariff?.price ?? finalPrice;
      const savingValue = Math.max(0, referenceTotal - finalPrice);
      const savingPercent = referenceTotal > 0 ? savingValue / referenceTotal : 0;
      const weights = this.familyWeights();

      return {
        lines,
        referenceTotal,
        target,
        marginSafe,
        totalCost,
        finalPrice,
        savingValue,
        savingPercent,
        tariff,
        weight: this.totalWeight(),
        weights,
      };
    }

    pickTariff(priceEuros) {
      if (!this.tariffs.length) return null;
      const sorted = [...this.tariffs].sort((a, b) => a.price - b.price);
      const ceil = sorted.find((t) => t.price + 1e-9 >= priceEuros);
      return ceil || sorted[sorted.length - 1];
    }

    setGrams(product, family, grams) {
      if (!this.canSetGrams(product, family, grams)) return false;
      const id = product.id;
      if (grams <= 0) {
        if (this.selections.has(id)) {
          this.selections.delete(id);
          track('pack_product_removed', { handle: product.handle });
        }
      } else {
        this.selections.set(id, { product, family, grams });
        track('pack_product_selected', { handle: product.handle, grams, family });
      }
      this.pricing = null;
      this.saveState();
      this.render();
      return true;
    }

    render() {
      const { loading, intro, build, calc, result } = this.els;
      if (loading) loading.hidden = this.phase !== 'loading';
      if (intro) intro.hidden = this.phase !== 'intro';
      if (build) build.hidden = this.phase !== 'build';
      if (calc) calc.hidden = this.phase !== 'calc';
      if (result) result.hidden = this.phase !== 'result';

      if (this.phase === 'build') {
        this.renderStatus();
        this.renderGuide();
        this.renderTabs();
        this.renderStep();
        this.renderNav();
      }
      if (this.phase === 'result' && this.pricing) this.renderResult();
    }

    renderTabs() {
      const w = this.familyWeights();
      this.els.tabs.forEach((tab) => {
        const idx = Number(tab.dataset.galPackTab);
        tab.classList.toggle('is-active', idx === this.stepIndex);
        tab.setAttribute('aria-selected', idx === this.stepIndex ? 'true' : 'false');
        const family = this.stepFamily(idx);
        const done = family ? w[family] >= this.minCat[family] : this.requirementsMet();
        tab.classList.toggle('is-done', Boolean(done && idx !== this.stepIndex));
      });
    }

    renderStatus() {
      const root = this.els.status;
      if (!root) return;
      const w = this.totalWeight();
      const left = Math.max(0, this.totalGrams - w);
      const fw = this.familyWeights();
      const pct = Math.min(100, (w / this.totalGrams) * 100);
      root.innerHTML = `
        <div class="gal-pack__meter-line">
          <p class="gal-pack__weight"><strong>${w}</strong> / ${this.totalGrams}G</p>
          <p class="gal-pack__remain">${left}G RESTANTS</p>
        </div>
        <div class="gal-pack__progress" role="progressbar" aria-valuemin="0" aria-valuemax="${this.totalGrams}" aria-valuenow="${w}">
          <span style="width:${pct}%"></span>
        </div>
        <ul class="gal-pack__reqs">
          ${this.familyKeys()
            .map((f) => this.reqItem(FAMILY_META[f].label, fw[f], this.minCat[f], this.maxCat[f]))
            .join('')}
        </ul>
      `;
    }

    reqItem(label, have, min, max) {
      const ok = have >= min;
      const maxed = have >= max;
      const mark = maxed ? ' MAX' : ok ? ' ✓' : '';
      return `<li class="gal-pack__req${ok ? ' is-ok' : ''}${maxed ? ' is-max' : ''}"><span>${label}</span><strong>${have} / ${min}–${max}G${mark}</strong></li>`;
    }

    renderGuide() {
      if (this.els.guide) this.els.guide.textContent = this.guidanceText();
    }

    renderStep() {
      if (this.stepIndex === 4) {
        this.renderFinalize();
        return;
      }
      const family = this.stepFamily(this.stepIndex);
      const products = this.catalog[family] || [];
      const fw = this.familyWeights()[family] || 0;
      const min = this.minCat[family];
      const max = this.maxCat[family];
      let state = `${min}–${max}G À COMPOSER`;
      if (fw >= max) state = 'MAXIMUM ATTEINT';
      else if (fw >= min) state = 'MINIMUM ATTEINT ✓';

      if (this.els.stepHead) {
        this.els.stepHead.innerHTML = `
          <div class="gal-pack__step-title-row">
            <h3 class="gal-pack__step-title">0${this.stepIndex + 1} — ${FAMILY_META[family].label}</h3>
            <p class="gal-pack__step-range${fw >= min ? ' is-ok' : ''}${fw >= max ? ' is-max' : ''}">
              <strong>${fw}G</strong> · ${state}
            </p>
          </div>
        `;
      }

      const grid = this.els.grid;
      if (!grid) return;
      if (!products.length) {
        grid.innerHTML = `<p class="gal-pack__empty">Configure 3 produits ${FAMILY_META[family].label} dans les réglages du thème.</p>`;
        return;
      }
      grid.className = 'gal-pack__grid gal-pack__grid--trio';
      grid.innerHTML = products.map((p) => this.miniCard(p, family)).join('');
      grid.querySelectorAll('[data-gal-pack-grams]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = Number(btn.dataset.productId);
          const grams = Number(btn.dataset.galPackGrams);
          const product = products.find((x) => x.id === id);
          if (!product) return;
          const current = this.selections.get(id)?.grams;
          this.setGrams(product, family, current === grams ? 0 : grams);
        });
      });
    }

    vuStrip(cbd) {
      if (cbd == null) return '';
      const segs = 12;
      const on = Math.max(1, Math.min(segs, Math.round((cbd / 100) * segs)));
      const cells = Array.from({ length: segs }, (_, i) => `<i class="${i < on ? 'is-on' : ''}"></i>`).join('');
      return `<div class="gal-pack__vu" aria-label="CBD ${cbd}%"><span>CBD ${cbd}%</span><div class="gal-pack__vu-track">${cells}</div></div>`;
    }

    miniCard(p, family) {
      const selected = this.selections.get(p.id);
      const grams = selected?.grams || 0;
      const perGram = p.p10 / 10;
      const cbd = parseNum(p.cbd);
      const badge =
        family === 'greenhouse'
          ? 'GREENHOUSE'
          : family === 'indoor'
            ? 'INDOOR'
            : family === 'rock'
              ? 'ROCK'
              : 'RÉSINE';
      const qtyBtns = this.gramSizes
        .map((g) => {
          const enable = grams === g ? true : this.canSetGrams(p, family, g);
          return `<button type="button" class="gal-pack__qty${grams === g ? ' is-active' : ''}" data-gal-pack-grams="${g}" data-product-id="${p.id}" ${enable ? '' : 'disabled'}>${g}G</button>`;
        })
        .join('');

      return `<article class="gal-pack__card${grams ? ' is-selected' : ''}${p.available ? '' : ' is-unavailable'}" data-product-id="${p.id}">
        <div class="gal-pack__card-media">${p.image ? `<img src="${p.image}" alt="" width="320" height="320" loading="lazy">` : ''}</div>
        <div class="gal-pack__card-body">
          <div class="gal-pack__card-top">
            <h3 class="gal-pack__card-title">${this.escape(p.title)}</h3>
            <span class="gal-pack__badge">${p.growing ? this.escape(String(p.growing)).toUpperCase() : badge}</span>
          </div>
          ${this.vuStrip(cbd)}
          <p class="gal-pack__card-price">${money(Math.round(perGram * 100))}/g <span>réf. 10G</span></p>
          <div class="gal-pack__qty-row" role="group" aria-label="Quantité">${p.available ? qtyBtns : '<p class="gal-pack__empty">Indisponible</p>'}</div>
        </div>
      </article>`;
    }

    renderFinalize() {
      const grid = this.els.grid;
      if (!grid) return;
      grid.className = 'gal-pack__grid gal-pack__grid--summary';
      const items = this.selectedList();
      const ok = this.requirementsMet();
      const fw = this.familyWeights();
      if (this.els.stepHead) {
        this.els.stepHead.innerHTML = `
          <div class="gal-pack__step-title-row">
            <h3 class="gal-pack__step-title">05 — RÉCAPITULATIF</h3>
            <p class="gal-pack__step-range${ok ? ' is-ok' : ''}"><strong>${this.totalWeight()}G</strong> / ${this.totalGrams}G</p>
          </div>
        `;
      }
      if (!items.length) {
        grid.innerHTML = `<p class="gal-pack__empty">Compose d’abord ton mix dans les quatre familles.</p>`;
        return;
      }
      const pricing = this.computePricing();
      grid.innerHTML = `
        <div class="gal-pack__summary">
          <ul class="gal-pack__family-split">
            ${this.familyKeys()
              .map(
                (f) =>
                  `<li class="${fw[f] >= this.minCat[f] ? 'is-ok' : ''}"><span>${FAMILY_META[f].label}</span><strong>${fw[f]}G</strong></li>`
              )
              .join('')}
          </ul>
          <ul class="gal-pack__summary-list">
            ${items
              .map(
                (i) => `<li>
                  ${i.product.image ? `<img src="${i.product.image}" alt="" width="56" height="56">` : ''}
                  <span><strong>${this.escape(i.product.title)}</strong> — ${i.grams}G · ${FAMILY_META[i.family].label}</span>
                </li>`
              )
              .join('')}
          </ul>
          <div class="gal-pack__summary-price">
            <p>Valeur normale <strong>${money(Math.round(pricing.referenceTotal * 100))}</strong></p>
            <p>Prix pack estimé <strong>${money(Math.round(pricing.finalPrice * 100))}</strong></p>
            <p>Économie estimée <strong>${money(Math.round(pricing.savingValue * 100))}</strong></p>
          </div>
          ${ok ? '' : `<p class="gal-pack__warn">${this.guidanceText()}</p>`}
        </div>
      `;
    }

    renderNav() {
      if (this.els.prev) this.els.prev.disabled = false;
      if (this.els.next) {
        this.els.next.hidden = this.stepIndex >= 4;
        this.els.next.disabled = false;
      }
      if (this.els.calculate) {
        this.els.calculate.hidden = this.stepIndex !== 4;
        this.els.calculate.disabled = !this.requirementsMet();
      }
    }

    async runFinalCalculation() {
      if (!this.requirementsMet()) return;
      track('pack_100g_completed', { weight: this.totalWeight(), weights: this.familyWeights() });
      this.phase = 'calc';
      this.render();
      const host = this.els.calc?.querySelector('[data-gal-needle]') || this.els.calc;
      const play = window.Galactica?.playNeedleMeter;
      try {
        if (typeof play === 'function' && host) await play(host, { duration: Math.max(1000, this.meterDuration) });
        else await new Promise((r) => setTimeout(r, 1100));
      } catch {
        /* continue */
      }
      this.pricing = this.computePricing();
      track('pack_price_calculated', {
        final: this.pricing.finalPrice,
        saving: this.pricing.savingValue,
      });
      this.phase = 'result';
      this.render();
    }

    renderResult() {
      const p = this.pricing;
      const body = this.els.resultBody;
      if (!body || !p) return;
      body.innerHTML = `
        <p class="gal-pack__validated">TON PACK — ${p.weight}G</p>
        <ul class="gal-pack__family-split gal-pack__family-split--result">
          ${this.familyKeys()
            .map((f) => `<li><span>${FAMILY_META[f].label}</span><strong>${p.weights[f]}G</strong></li>`)
            .join('')}
        </ul>
        <div class="gal-pack__totals">
          <div>
            <span>VALEUR NORMALE</span>
            <strong>${money(Math.round(p.referenceTotal * 100))}</strong>
          </div>
          <div class="gal-pack__totals-main">
            <span>TON PRIX PACK</span>
            <strong>${money(Math.round(p.finalPrice * 100))}</strong>
          </div>
          <div>
            <span>TU ÉCONOMISES</span>
            <strong>${money(Math.round(p.savingValue * 100))}</strong>
          </div>
        </div>
        <ul class="gal-pack__summary-list">
          ${p.lines
            .map(
              (i) => `<li>
                ${i.image ? `<img src="${i.image}" alt="" width="56" height="56">` : ''}
                <span><strong>${this.escape(i.title)}</strong> — ${i.grams}G · ${FAMILY_META[i.family].label}</span>
              </li>`
            )
            .join('')}
        </ul>
      `;
    }

    async addToCart() {
      if (!this.pricing?.tariff?.id) {
        alert('Pack indisponible pour le moment (tarif parent manquant).');
        return;
      }
      const p = this.pricing;
      const contenu = p.lines.map((l) => `${l.title} — ${l.grams}G`).join(' · ');
      const packJson = {
        v: 2,
        weight: p.weight,
        weights: p.weights,
        reference: p.referenceTotal,
        final: p.finalPrice,
        saving: p.savingValue,
        items: p.lines.map((l) => ({
          id: l.id,
          handle: l.handle,
          title: l.title,
          grams: l.grams,
          family: l.family,
          p10: l.p10,
        })),
      };

      const btn = this.els.add;
      if (btn) btn.disabled = true;
      try {
        const res = await fetch(window.Galactica.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: Number(p.tariff.id),
                quantity: 1,
                properties: {
                  Pack: 'CRÉE TON PACK FRÈRE — 100G',
                  Contenu: contenu.slice(0, 450),
                  'Valeur normale': money(Math.round(p.referenceTotal * 100)),
                  Économie: money(Math.round(p.savingValue * 100)),
                  _pack_json: JSON.stringify(packJson),
                },
              },
            ],
          }),
        });
        if (!res.ok) throw new Error('cart_add_failed');
        await fetch(window.Galactica.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            attributes: {
              pack_frere: '1',
              pack_frere_summary: contenu.slice(0, 200),
            },
          }),
        }).catch(() => null);

        sessionStorage.removeItem(STORAGE_KEY);
        track('pack_added_to_cart', { final: p.finalPrice });
        document.dispatchEvent(new CustomEvent('gal:cart:refresh'));
        document.dispatchEvent(new CustomEvent('gal:cart:open'));
        this.dialog?.querySelector('[data-gal-pack-close]')?.click();
      } catch (err) {
        console.error(err);
        alert('Impossible d’ajouter le pack au panier. Réessaie.');
      } finally {
        if (btn) btn.disabled = false;
      }
    }

    saveState() {
      const payload = {
        stepIndex: this.stepIndex,
        items: this.selectedList().map((i) => ({
          id: i.product.id,
          family: i.family,
          grams: i.grams,
        })),
      };
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch {
        /* ignore */
      }
    }

    hasSavedState() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        const data = JSON.parse(raw);
        return Array.isArray(data.items) && data.items.length > 0;
      } catch {
        return false;
      }
    }

    loadState() {
      try {
        const data = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
        if (!data?.items) return;
        this.selections.clear();
        const all = [
          ...this.catalog.greenhouse.map((p) => ({ p, f: 'greenhouse' })),
          ...this.catalog.indoor.map((p) => ({ p, f: 'indoor' })),
          ...this.catalog.resin.map((p) => ({ p, f: 'resin' })),
          ...this.catalog.rock.map((p) => ({ p, f: 'rock' })),
        ];
        for (const item of data.items) {
          const hit = all.find((x) => x.p.id === item.id && x.f === item.family);
          if (!hit) continue;
          if (!this.gramSizes.includes(item.grams) || item.grams > this.maxPerProduct) continue;
          this.selections.set(item.id, { product: hit.p, family: item.family, grams: item.grams });
        }
        if (!this.isFeasible(this.familyWeights())) this.selections.clear();
        this.stepIndex = Number(data.stepIndex || 0);
      } catch {
        /* ignore */
      }
    }

    tryResumeBanner() {
      if (this.els.resume) this.els.resume.hidden = !this.hasSavedState();
    }

    escape(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  if (!customElements.get('gal-pack-builder')) {
    customElements.define('gal-pack-builder', GalPackBuilder);
  }

  window.Galactica = window.Galactica || {};
  window.Galactica.PackBuilder = GalPackBuilder;
})();
